const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api'

// In-memory auth token — set after magic-link callback
let _authToken: string | null = null

/** Called by the auth store to set/clear the JWT */
export function setAuthToken(token: string | null) {
  _authToken = token
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> ?? {}),
  }
  if (_authToken) {
    headers['Authorization'] = `Bearer ${_authToken}`
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    headers,
    ...init,
  })

  if (!res.ok) {
    let body: unknown
    try {
      body = await res.json()
    } catch {
      body = null
    }
    const message =
      (body as { error?: string } | null)?.error ?? `HTTP ${res.status}`
    throw new ApiError(res.status, message, body)
  }

  if (res.status === 204) return undefined as unknown as T
  return res.json() as Promise<T>
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export interface User {
  id: string
  email: string
  role: 'admin' | 'user'
  lastLogin: string | null
  createdAt: string
}

export const auth = {
  requestMagicLink: (email: string) =>
    request<{ message: string }>('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  getCurrentUser: () => request<User>('/auth/me'),

  logout: () =>
    request<void>('/auth/logout', { method: 'POST' }),
}

// ─── Uploads ─────────────────────────────────────────────────────────────────

export interface PresignedUpload {
  uploadId: string
  r2Key: string
  uploadUrl: string
  fields: Record<string, string>
}

export interface Upload {
  id: string
  filename: string
  contentType: string
  sizeBytes: number
  r2Key: string
  createdAt: string
}

export const uploads = {
  presignUpload: (filename: string, contentType: string) =>
    request<PresignedUpload>('/uploads/presign', {
      method: 'POST',
      body: JSON.stringify({ filename, contentType }),
    }),

  completeUpload: (uploadId: string, r2Key: string) =>
    request<Upload>('/uploads/complete', {
      method: 'POST',
      body: JSON.stringify({ uploadId, r2Key }),
    }),

  listUploads: () => request<Upload[]>('/uploads'),

  deleteUpload: (id: string) =>
    request<void>(`/uploads/${id}`, { method: 'DELETE' }),
}

// ─── Jobs ─────────────────────────────────────────────────────────────────────

export type OutputType = 'sop' | 'agent_prompt' | 'website' | 'blog' | 'course_module'
export type JobStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled'
export type PhaseStatus = 'pending' | 'active' | 'done' | 'failed'

export interface JobPhase {
  name: string
  label: string
  status: PhaseStatus
  progress: number
  startedAt: string | null
  completedAt: string | null
}

export interface Job {
  id: string
  uploadId: string
  filename: string
  status: JobStatus
  outputs: OutputType[]
  phases: JobPhase[]
  currentPhase: string | null
  progress: number
  errorMessage: string | null
  division: string | null
  title: string | null
  description: string | null
  createdAt: string
  completedAt: string | null
}

export interface CreateJobParams {
  uploadId: string
  outputs: OutputType[]
  title?: string
  description?: string
  division?: string
}

export interface JobProgressEvent {
  type: 'progress' | 'phase_change' | 'complete' | 'error'
  jobId: string
  status: JobStatus
  phase: string | null
  progress: number
  message?: string
}

export const jobs = {
  createJob: (params: CreateJobParams) =>
    request<Job>('/jobs', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  listJobs: () => request<Job[]>('/jobs'),

  getJob: (id: string) => request<Job>(`/jobs/${id}`),

  streamJobProgress: (
    id: string,
    onEvent: (event: JobProgressEvent) => void,
    onError?: (err: Error) => void
  ): (() => void) => {
    const es = new EventSource(`${BASE_URL}/jobs/${id}/stream`, {
      withCredentials: true,
    } as EventSourceInit)

    es.onmessage = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data as string) as JobProgressEvent
        onEvent(data)
      } catch {
        // ignore parse errors
      }
    }

    es.onerror = () => {
      onError?.(new Error('SSE connection error'))
      es.close()
    }

    return () => es.close()
  },

  cancelJob: (id: string) =>
    request<void>(`/jobs/${id}/cancel`, { method: 'POST' }),
}

// ─── Outputs ─────────────────────────────────────────────────────────────────

export interface JobOutput {
  id: string
  jobId: string
  type: OutputType
  filename: string
  sizeBytes: number
  r2Key: string
  createdAt: string
}

export const outputs = {
  listOutputs: (jobId: string) =>
    request<JobOutput[]>(`/jobs/${jobId}/outputs`),

  getDownloadUrl: (id: string) =>
    request<{ url: string }>(`/outputs/${id}/download`),

  getPreviewUrl: (id: string) =>
    request<{ url: string }>(`/outputs/${id}/preview`),
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export interface UsageSummary {
  totalCostUsd: number
  totalJobs: number
  byService: Array<{ service: string; costUsd: number; calls: number }>
  byUser: Array<{ userId: string; email: string; costUsd: number; jobs: number }>
}

export const admin = {
  listUsers: () => request<User[]>('/admin/users'),

  updateUser: (id: string, role: 'admin' | 'user') =>
    request<User>(`/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),

  getUsage: (from: string, to: string) =>
    request<UsageSummary>(`/admin/usage?from=${from}&to=${to}`),

  listAllJobs: () => request<Job[]>('/admin/jobs'),

  getSettings: () => request<Record<string, string>>('/admin/settings'),

  updateSetting: (key: string, value: string) =>
    request<void>('/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify({ key, value }),
    }),
}

export { ApiError }
